import { Component, inject, Input, OnInit } from '@angular/core';
import { CategoryStore } from '../../../stores/category/category.state';
import { ImportData, ImportDataValidation } from '../../../models/import-data';
import { AccordionModule } from 'primeng/accordion';
import { ChipModule } from 'primeng/chip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { DecimalPipe, NgIf } from '@angular/common';
import { SharedModule } from 'primeng/api';
import { SafeNumberPipe } from '../../../pipes/safe-number.pipe';
import { DataTableComponent } from '../data-table/data-table.component';
import { ComparisonValueStore } from '../../../stores/comparison-value/comparison-value.state';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { FeatureProperties } from '../../../models/geo-data';

@Component({
  selector: 'app-data-import-list',
  standalone: true,
  imports: [
    AccordionModule,
    ChipModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    NgIf,
    SharedModule,
    DataTableComponent,
    AlertComponent,
  ],
  providers: [DecimalPipe, SafeNumberPipe],
  templateUrl: './data-import-list.component.html',
  styleUrl: './data-import-list.component.scss',
})
export class DataImportListComponent implements OnInit {
  readonly categoryStore = inject(CategoryStore);
  readonly comparisonStore = inject(ComparisonValueStore);
  @Input() items: ImportData[] = [];
  @Input() severity = '';
  @Input() icon = '';
  @Input() validations?: ImportDataValidation;
  @Input() yearlyValidations?: { [key: string]: ImportDataValidation };
  compKeys: string[] = [];
  itemsFiltered: ImportData[] = [];

  precomputedDistricts: {
    [key: string]: { [key: string]: FeatureProperties };
  } = {};

  /**
   * Lifecycle hook that is called after Angular has initialized the component.
   * This method performs the following tasks:
   * 1. Initializes the `itemsFiltered` array as a copy of the `items` array.
   * 2. Obtains non-default keys from the comparison store and assigns them to `compKeys`.
   * 3. Precomputes districts by invoking `precomputeDistricts`.
   *
   * @return {void} Does not return a value.
   */
  ngOnInit(): void {
    this.itemsFiltered = [...this.items];
    this.compKeys = this.comparisonStore.keysWithoutDefaults();
    this.precomputeDistricts(); // Run computation on initialization
  }

  /**
   * Updates the list of items based on the provided search query by filtering the items
   * that match the query. If the search query is empty, it resets the filtered items
   * to the complete list. After updating the filtered list, it precomputes districts.
   *
   * @param {string} searchQuery - The search query used to filter the list of items.
   *                                It is case-insensitive.
   * @return {void} Returns nothing.
   */
  updateList(searchQuery: string): void {
    if (!searchQuery.length) {
      this.itemsFiltered = this.items;
    } else {
      this.itemsFiltered = this.items.filter((item: ImportData) => {
        return item.importName
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase());
      });
    }
    this.precomputeDistricts();
  }

  /**
   * Precomputes the districts for each item in the itemsFiltered array
   * and stores the result in the precomputedDistricts object.
   *
   * @return {void} This method does not return a value.
   */
  precomputeDistricts(): void {
    this.itemsFiltered.forEach(item => {
      this.precomputedDistricts[item.key] = this.getDistricts(item);
    });
  }

  /**
   * Retrieves the districts from the provided data object, excluding any keys that match the component's keys.
   *
   * @param {ImportData} item - The data object containing district information and properties.
   * @return {Object<string, FeatureProperties>} An object containing the filtered districts with their associated properties.
   */
  getDistricts(item: ImportData): { [key: string]: FeatureProperties } {
    const districts = { ...item.districts };
    this.compKeys.forEach(key => {
      delete districts[key];
    });
    return districts;
  }
}
